import React, { useEffect } from "react";
import { Alert, Spin, Tag } from "antd";
import usePayrollPreview from "./usePayrollPreview";
import { formatCents } from "./payrollOptions";
import "./Payroll.css";

import { useTranslation } from "react-i18next";
/**
 * One-line "this is what the employee takes home" preview, for forms that only collect the
 * Lohnsteuerabzugsmerkmale and have no room for the full breakdown.
 *
 * @param {object} props
 * @param {string} [props.employeeId] Employee whose stored settings fill in the rest.
 * @param {object} props.values       At least { brutto, steuerklasse }.
 */
const InlineNetPreview = ({ employeeId, values }) => {
  const { t } = useTranslation("dashboard");
  const { payroll, loading, inputError, request } = usePayrollPreview(employeeId);
  // The values object is rebuilt on every keystroke, so compare by content rather than identity.
  const valuesKey = JSON.stringify(values || {});

  useEffect(() => {
    request(values);
  }, [valuesKey]);

  if (inputError) return <Alert type="error" showIcon message={inputError} />;
  if (!payroll && !loading) {
    return (
      <Alert
        type="info"
        showIcon
        message={t("Enter a gross salary and a Steuerklasse to see the Lohnsteuer and net pay.")}
      />
    );
  }

  return (
    <Spin spinning={loading}>
      {payroll ? (
        <div className="payroll-inline">
          <Tag color="blue">
            {t("Lohnsteuer")} {formatCents(payroll.tax.lohnsteuer)}
          </Tag>
          {payroll.tax.solidaritaetszuschlag > 0 ? (
            <Tag>
              {t("SolZ")} {formatCents(payroll.tax.solidaritaetszuschlag)}
            </Tag>
          ) : null}
          {payroll.tax.kirchensteuer > 0 ? (
            <Tag>
              {t("KiSt")} {formatCents(payroll.tax.kirchensteuer)}
            </Tag>
          ) : null}
          <Tag>
            {t("Social insurance")} {formatCents(payroll.socialInsurance.employee.total)}
          </Tag>
          <Tag color="green">
            {t("Net")} {formatCents(payroll.netto)}
          </Tag>
          <Tag color="purple">
            {t("Total employer cost")} {formatCents(payroll.employerCost)}
          </Tag>
        </div>
      ) : null}
    </Spin>
  );
};

export default InlineNetPreview;
